import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Eye, Play, ArrowLeft, Hash, Heart, Loader2, ShoppingCart, Check, ChevronDown, ExternalLink, AppWindow, X, Share2 } from 'lucide-react';
import { useGamesStore } from '../store/useGamesStore';
import { useThemeStore } from '../store/useThemeStore';
import ScreenshotCarousel from '../components/game/ScreenshotCarousel';
import GameCard from '../components/game/GameCard';
import GameGrid from '../components/game/GameGrid';
import Button from '../components/common/Button';
import CategoryTag from '../components/game/CategoryTag';
import toast from 'react-hot-toast';
import { getCategoryDisplayName } from '../types/game';
import { useAnalytics } from '../hooks/useAnalytics';
import { purchaseService } from '../services/purchaseService';
import { formatCurrency } from '../utils/currency';

// Default placeholder image
// Default placeholder - clean light gray with centered Lucide Gamepad2 icon
const DEFAULT_GAME_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"%3E%3Crect width="400" height="300" fill="%23f1f5f9"/%3E%3Cg transform="translate(200,150) scale(4.7) translate(-12,-12)"%3E%3Cline x1="6" y1="11" x2="10" y2="11" stroke="%2364748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/%3E%3Cline x1="8" y1="9" x2="8" y2="13" stroke="%2364748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/%3E%3Cline x1="15" y1="12" x2="15.01" y2="12" stroke="%2364748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/%3E%3Cline x1="18" y1="10" x2="18.01" y2="10" stroke="%2364748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/%3E%3Cpath d="M17.32 5H6.68a4 4 0 0 0-3.978 3.59c-.006.052-.01.101-.017.152C2.604 9.416 2 14.456 2 16a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.414-1.414A2 2 0 0 1 9.828 16h4.344a2 2 0 0 1 1.414.586L17 18c.5.5 1 1 2 1a3 3 0 0 0 3-3c0-1.545-.604-6.584-.685-7.258-.007-.05-.011-.1-.017-.151A4 4 0 0 0 17.32 5z" fill="none" stroke="%2364748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/%3E%3C/g%3E%3C/svg%3E';

export default function GameDetailPage() {
  const { gameId } = useParams<{ gameId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { trackGameAction } = useAnalytics();
  const { isDark } = useThemeStore();
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [pageLoadTime] = useState(Date.now());
  const [checkoutStartTime, setCheckoutStartTime] = useState<number | null>(null);
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const gamePopupRef = useRef<Window | null>(null);
  
  const {
    currentGame,
    isLoadingGame,
    gameError,
    fetchGameById,
    getSimilarGames,
    clearCurrentGame,
    fetchCatalog,
    hasLoadedCatalog,
    userTeams,
  } = useGamesStore();

  // Scroll to top when page loads or gameId changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [gameId]);

  const game = currentGame;

  // Handle purchase completion redirect
  useEffect(() => {
    const purchaseStatus = searchParams.get('purchase');
    
    if (purchaseStatus === 'success') {
      toast.success('🎉 Purchase successful! You now own this game.');
      
      // Track checkout completed
      if (game) {
        const timeInCheckout = checkoutStartTime ? Date.now() - checkoutStartTime : 0;
        trackGameAction('checkout_completed', game.id.toString(), {
          amount: game.pricing?.price || 0,
          currency: game.pricing?.currency || 'USD',
          time_in_checkout_ms: timeInCheckout
        });
        
        // Store purchase timestamp for first-play tracking
        localStorage.setItem(`beemi_purchase_${game.id}`, Date.now().toString());
      }
      
      // Refetch game to update ownership status
      if (gameId) {
        fetchGameById(gameId);
      }
      // Remove query parameter from URL
      searchParams.delete('purchase');
      setSearchParams(searchParams, { replace: true });
      setCheckoutStartTime(null);
    } else if (purchaseStatus === 'cancelled') {
      toast.error('Purchase was cancelled.');
      
      // Track checkout cancelled
      if (game) {
        const timeInCheckout = checkoutStartTime ? Date.now() - checkoutStartTime : 0;
        trackGameAction('checkout_cancelled', game.id.toString(), {
          time_in_checkout_ms: timeInCheckout
        });
      }
      
      // Remove query parameter from URL
      searchParams.delete('purchase');
      setSearchParams(searchParams, { replace: true });
      setCheckoutStartTime(null);
    }
  }, [searchParams, gameId, game, checkoutStartTime, fetchGameById, setSearchParams, trackGameAction]);

  // Fetch game and catalog on mount
  useEffect(() => {
    if (gameId) {
      fetchGameById(gameId);
    }
    if (!hasLoadedCatalog) {
      fetchCatalog();
    }
    
    return () => {
      clearCurrentGame();
    };
  }, [gameId, fetchGameById, clearCurrentGame, fetchCatalog, hasLoadedCatalog]);

  // Track game detail page view
  useEffect(() => {
    if (game && !isLoadingGame) {
      // Check if user is part of the team
      const isTeamMember = game.team_id && userTeams?.some(team => team.id === game.team_id);
      
      // Determine source from referrer
      const referrer = document.referrer;
      let source: 'home' | 'categories' | 'search' | 'my_games' | 'featured' | 'direct' = 'direct';
      if (referrer.includes('/categories')) source = 'categories';
      else if (referrer.includes('/search')) source = 'search';
      else if (referrer.includes('/my-games')) source = 'my_games';
      else if (referrer.includes('/?') || referrer.endsWith('/')) source = 'home';
      
      trackGameAction('game_detail_viewed', game.id.toString(), {
        game_name: game.name,
        category: game.tags?.[0] || 'unknown',
        is_paid: game.pricing?.is_paid || false,
        price: game.pricing?.price,
        currency: game.pricing?.currency || 'USD',
        already_owned: game.user_owns || false,
        is_team_member: isTeamMember || false,
        source
      });
    }
  }, [game, isLoadingGame, userTeams, trackGameAction]);
  const similarGames = gameId ? getSimilarGames(gameId, 4) : [];

  const handlePlayGame = () => {
    if (!game) return;
    
    trackGameAction('play_button_clicked', game.id.toString(), {
      game_name: game.name,
      source: 'game_detail_page'
    });
    
    // Calculate popup dimensions for game-only mode (4:5 content area)
    // Add ~60px to height to account for browser chrome (title bar + address bar)
    const browserChrome = 60;
    const contentHeight = Math.round(window.screen.availHeight * 0.85) - browserChrome;
    const contentWidth = Math.round(contentHeight * (4 / 4.8));
    const width = contentWidth;
    const height = contentHeight + browserChrome;

    // Center the popup on the screen
    const left = Math.round((window.screen.width - width) / 2);
    const top = Math.round((window.screen.height - height) / 2);

    // Open popup window with game in original (game-only) mode
    const popup = window.open(
      `/play/${game.id}`,
      'beemi-game-player',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=yes`
    );
    
    if (!popup) {
      setPopupBlocked(true);
      gamePopupRef.current = null;
    } else {
      setPopupBlocked(false);
      gamePopupRef.current = popup;
    }
    setShowLaunchModal(true);
  };

  const handleShareGame = () => {
    if (!game) return;
    navigator.clipboard.writeText(window.location.href).then(() => {
      toast.success('Link copied to clipboard!');
    }).catch(() => {
      toast.error('Could not copy link');
    });
  };

  const handlePurchaseGame = async () => {
    if (!game) return;
    
    setIsPurchasing(true);
    const timeOnPage = Date.now() - pageLoadTime;
    
    try {
      trackGameAction('purchase_initiated', game.id.toString(), {
        game_name: game.name,
        price: game.pricing?.price || 0,
        currency: game.pricing?.currency || 'USD',
        time_on_page_ms: timeOnPage
      });
      
      // Call API to initiate purchase
      const response = await purchaseService.initiatePurchase(game.id);
      
      if (!response.success || !response.data) {
        toast.error(response.message || 'Failed to initiate purchase');
        
        trackGameAction('checkout_failed', game.id.toString(), {
          game_name: game.name,
          error_type: 'api_error',
          error_message: response.message || 'Failed to initiate purchase',
          stage: 'session_creation'
        });
        
        setIsPurchasing(false);
        return;
      }
      
      // Load Stripe
      // @ts-ignore - Stripe is loaded from CDN
      const stripe = window.Stripe ? window.Stripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY) : null;
      
      if (!stripe) {
        toast.error('Payment system not available. Please refresh the page.');
        
        trackGameAction('checkout_failed', game.id.toString(), {
          game_name: game.name,
          error_type: 'stripe_error',
          error_message: 'Payment system not available',
          stage: 'stripe_redirect'
        });
        
        setIsPurchasing(false);
        return;
      }
      
      // Set checkout start time for measuring time in Stripe
      setCheckoutStartTime(Date.now());
      
      // Redirect to Stripe Checkout
      const { error } = await stripe.redirectToCheckout({
        sessionId: response.data.sessionId,
      });
      
      if (error) {
        console.error('Stripe error:', error);
        toast.error(error.message || 'Payment failed');
        
        trackGameAction('checkout_failed', game.id.toString(), {
          game_name: game.name,
          error_type: 'stripe_error',
          error_message: error.message || 'Payment failed',
          stage: 'stripe_redirect'
        });
        
        setCheckoutStartTime(null);
      }
    } catch (error: any) {
      console.error('Purchase error:', error);
      toast.error('Failed to initiate purchase');
      
      trackGameAction('checkout_failed', game.id.toString(), {
        game_name: game.name,
        error_type: 'network_error',
        error_message: error.message || 'Unknown error',
        stage: 'session_creation'
      });
    } finally {
      setIsPurchasing(false);
    }
  };
  
  // Check if user is part of the team that created this game
  const isTeamMember = game?.team_id && userTeams?.some(team => team.id === game.team_id);
  
  // Check if user needs to purchase this game
  const needsToPurchase = game?.pricing?.is_paid && !game?.user_owns && !isTeamMember;
  const isOwned = game?.user_owns || !game?.pricing?.is_paid || isTeamMember;

  // Loading state
  if (isLoadingGame) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-12 h-12 animate-spin text-primary-500" />
      </div>
    );
  }

  // Error or not found
  if (gameError || !game) {
    return (
      <div className="text-center py-12">
        <h1 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
          {gameError || 'Game not found'}
        </h1>
        <Link to="/">
          <Button>Go Home</Button>
        </Link>
      </div>
    );
  }

  // Get the best available image
  const imageUrl = game.icon_url || game.cover_image_url || DEFAULT_GAME_IMAGE;
  
  // Get author display name
  const authorName = game.author?.name || game.team_name || 'Unknown';
  const authorPrefix = game.author?.is_team ? '🏢 ' : '@';

  return (
    <div className="space-y-8">
      {/* Back Button */}
      <Link to="/" className="inline-flex items-center gap-2 text-muted hover:text-gray-900 dark:hover:text-white transition-colors">
        <ArrowLeft className="w-4 h-4" />
        Back to games
      </Link>

      {/* Main Content */}
      <div className="flex flex-wrap justify-center gap-8">
        {/* Left: Screenshots */}
        <div className="w-[400px]">
          <ScreenshotCarousel 
            screenshots={game.screenshot_urls || []}
            gameName={game.name}
            iconUrl={game.icon_url}
            coverImageUrl={game.cover_image_url}
          />
        </div>

        {/* Right: Game Info */}
        <div className="space-y-6">
          <div className="flex items-start gap-4">
            {/* Game Icon */}
            {game.icon_url && (
              <img
                src={game.icon_url}
                alt={`${game.name} icon`}
                className="w-32 h-32 rounded-xl object-cover flex-shrink-0"
              />
            )}
            
            {/* Title, Author and Stats */}
            <div className="flex-1 min-w-0 space-y-4">
              <div>
                <h1 className="text-3xl font-bold mb-2 text-gray-900 dark:text-white">
                  {game.name}
                </h1>
                <div className="flex items-center gap-2 text-lg text-muted">
                  <span>by</span>
                  {game.author?.image ? (
                    <img 
                      src={game.author.image} 
                      alt={authorName}
                      className="w-6 h-6 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#FCB902] to-[#FF00CF] flex items-center justify-center">
                      <span className="text-white text-xs font-bold">
                        {authorName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <span className="font-medium">{authorName}</span>
                </div>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Play className="w-4 h-4 text-muted" />
              <span className="text-lg font-bold text-gray-900 dark:text-white">
                {(game.total_plays || 0).toLocaleString()}
              </span>
              <span className="text-sm text-muted">plays</span>
            </div>
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-muted" />
              <span className="text-lg font-bold text-gray-900 dark:text-white">
                {(game.total_plays || 0).toLocaleString()}
              </span>
              <span className="text-sm text-muted">views</span>
            </div>
            <div className="flex items-center gap-2">
              <Heart className="w-4 h-4 text-muted" />
              <span className="text-lg font-bold text-gray-900 dark:text-white">
                {(game.total_likes || 0).toLocaleString()}
              </span>
              <span className="text-sm text-muted">likes</span>
            </div>
          </div>
            </div>
          </div>
          
          {/* Owned Badge (if user owns the game) */}
          {game.pricing?.is_paid && game.user_owns && (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-400 to-emerald-500 text-white rounded-lg font-bold text-lg shadow-lg">
              <Check className="w-5 h-5" />
              Owned
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            {needsToPurchase ? (
              <Button
                onClick={handlePurchaseGame}
                className="flex-1 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white border-none"
                size="lg"
                disabled={isPurchasing}
              >
                {isPurchasing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Buy for {formatCurrency(game.pricing?.price || 0, game.pricing?.currency || 'USD')}
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handlePlayGame}
                className="flex-1"
                size="lg"
              >
                <Play className="w-5 h-5 mr-2" />
                Play Now
              </Button>
            )}
            <Button
              onClick={handleShareGame}
              variant="outline"
              size="lg"
              className="px-4"
              title="Copy link"
            >
              <Share2 className="w-5 h-5" />
            </Button>
          </div>

          {/* Description */}
          <div>
            <h3 className="text-sm font-semibold mb-2 text-gray-900 dark:text-white uppercase tracking-wide">
              Description
            </h3>
            <p className="text-muted leading-relaxed">
              {game.description || game.short_description || 'No description available'}
            </p>
          </div>

          {/* Categories */}
          {game.tags && game.tags.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-2 text-gray-900 dark:text-white uppercase tracking-wide">
                Categories
              </h3>
              <div className="flex flex-wrap gap-2">
                {game.tags.map((tag) => (
                  <CategoryTag key={tag} categoryId={tag} size="md" />
                ))}
              </div>
            </div>
          )}

          {/* Version & Updated Date */}
          {(game.version || game.updated_at) && (
            <div className="text-sm text-muted">
              {game.version && (
                <>
                  <span className="font-bold text-gray-900 dark:text-white">Version {game.version}</span>
                  {game.updated_at && ': '}
                </>
              )}
              {game.updated_at && (
                <>
                  Updated {new Date(game.updated_at).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Similar Games */}
      {similarGames.length > 0 && (
        <section>
          <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
            Similar Games You Might Like
          </h2>
          <GameGrid>
            {similarGames.map((similarGame) => (
              <GameCard key={similarGame.id} game={similarGame} />
            ))}
          </GameGrid>
        </section>
      )}

      {/* Game Launch Modal */}
      {showLaunchModal && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" onClick={() => setShowLaunchModal(false)}>
          <div className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-border">
              <div className="flex items-center gap-3 min-w-0">
                {game?.icon_url && (
                  <img src={game.icon_url} alt={game.name} className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
                )}
                <h2 className="text-lg font-bold text-gray-900 dark:text-white truncate">
                  <span>{game?.name}</span>
                  <span className="text-gray-400 dark:text-gray-500 font-normal">: How to Stream</span>
                </h2>
              </div>
              <button onClick={() => setShowLaunchModal(false)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-200 dark:divide-dark-border h-[calc(85vh-65px)] overflow-hidden">

              {/* Left: Launch status + popup help */}
              <div className="p-6 space-y-5 overflow-y-auto h-full min-h-0">
                {/* Status banner */}
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${popupBlocked ? 'bg-red-500/20' : 'bg-green-500/20'}`}>
                    <AppWindow className={`w-5 h-5 ${popupBlocked ? 'text-red-500 dark:text-red-400' : 'text-green-500 dark:text-green-400'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {popupBlocked ? 'Popup was blocked' : 'Game opened in a new window'}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      {popupBlocked
                        ? 'Your browser blocked the game window from opening.'
                        : `${game?.name} is running in a separate window. Keep it open while you play.`}
                    </p>
                    {!popupBlocked && (
                      <div className="flex justify-end mt-2">
                        <button
                          onClick={() => {
                            if (gamePopupRef.current && !gamePopupRef.current.closed) {
                              gamePopupRef.current.focus();
                            }
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-500/10 hover:bg-green-500/20 text-green-600 dark:text-green-400 text-xs font-medium transition-colors"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          Go To Window
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Popup blocked instructions — always visible as a guide */}
                <div className="rounded-xl border border-gray-200 dark:border-dark-border overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 dark:bg-dark-bg border-b border-gray-200 dark:border-dark-border">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Popup not opening?</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      If you see a "Popup blocked" message, follow these steps to allow it:
                    </p>
                  </div>
                  <div className="p-4 space-y-3">
                    <ol className="space-y-2">
                      {[
                        'Click the options or blocked popup icon in your browser\'s address bar',
                        'Find the "Pop-ups" or "Popups" toggle and turn it ON for this site',
                        'Reload the page and click Play again',
                      ].map((step, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-300">
                          <span className="w-5 h-5 rounded-full bg-primary-500/20 text-primary-500 dark:text-primary-400 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                          {step}
                        </li>
                      ))}
                    </ol>
                    <img
                      src="/images/instructions/allow-popup.png"
                      alt="How to allow popups in your browser"
                      className="w-2/3 mx-auto block rounded-lg border border-gray-200 dark:border-dark-border mt-2"
                    />
                  </div>
                </div>

                {!popupBlocked && (
                  <div className="rounded-lg bg-gray-50 dark:bg-dark-bg p-4 space-y-2">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Tips</p>
                    <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1.5 list-disc list-inside">
                      <li>Position the game window where your audience can see it</li>
                      <li>Use the layout button in the game window to switch between views</li>
                      <li>Connect your stream using the button in the game window's toolbar</li>
                    </ul>
                  </div>
                )}
              </div>

              {/* Right: Streaming instructions + switch game */}
              <div className="flex flex-col min-h-0 h-full">
                {/* Accordion — scrollable */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">Stream Setup</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">How to capture and stream this game window</p>
                  </div>

                  {[
                    {
                      id: 'obs',
                      label: 'OBS Studio',
                      steps: [
                        'Open OBS and click "+" under Sources',
                        'Select "Window Capture"',
                        'In the window dropdown, select the Beemi game window',
                        'Click OK — the game will appear in your scene',
                        'Click "Start Streaming" when ready',
                      ],
                    },
                    {
                      id: 'streamlabs',
                      label: 'Streamlabs',
                      steps: [
                        'Open Streamlabs and go to the Editor',
                        'Click "+" to add a source → choose "Window Capture"',
                        'Select the Beemi game window from the list',
                        'Resize and position it in your scene',
                        'Hit "Go Live" to start your stream',
                      ],
                    },
                    {
                      id: 'tiktok',
                      label: 'TikTok Live Studio',
                      steps: [
                        'Open TikTok Live Studio and create a new scene',
                        'Click "Add Source" → select "Window Capture"',
                        'Choose the Beemi game window',
                        'Adjust the capture area to fit your layout',
                        'Press "Start Live" to go live on TikTok',
                      ],
                    },
                  ].map(({ id, label, steps }) => (
                    <div key={id} className="border border-gray-200 dark:border-dark-border rounded-xl overflow-hidden">
                      <button
                        className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-dark-bg transition-colors"
                        onClick={() => setOpenAccordion(openAccordion === id ? null : id)}
                      >
                        <span>{label}</span>
                        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${openAccordion === id ? 'rotate-180' : ''}`} />
                      </button>
                      {openAccordion === id && (
                        <div className="px-4 pb-4 bg-gray-50 dark:bg-dark-bg">
                          <ol className="space-y-2 mt-2">
                            {steps.map((step, i) => (
                              <li key={i} className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-300">
                                <span className="w-5 h-5 rounded-full bg-primary-500/20 text-primary-500 dark:text-primary-400 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                                {step}
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}
                    </div>
                  ))}

                  <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" />
                    After capturing, click "Connect Stream" in the game window to link your live chat.
                  </p>
                </div>

                {/* Switch Game — pinned at bottom */}
                <div className="flex-shrink-0 border-t border-gray-200 dark:border-dark-border px-6 py-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Want to play something else?</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">You can switch games at any time — your stream stays running.</p>
                  </div>
                  <Link
                    to="/"
                    onClick={() => setShowLaunchModal(false)}
                    className="flex-shrink-0 px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-sm font-semibold text-gray-900 dark:text-white transition-colors whitespace-nowrap"
                  >
                    Switch Game
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
